import { capSQLiteSet } from "@capacitor-community/sqlite";
import { Feature } from "geojson";
import { CoreoCollectionItem } from "../../../types";
import { SyncItem } from "../app-sync.service";
import SQLDatabase, { SQLQueryBuilder } from "../sql.service";
import { AppDatabaseRepo } from "./base.db";

export interface AppDatabaseItem {
  id: number;
  key: string;
  value: string;
  data: string;
  projectId: number;
  collectionId: number;
  createdAt: string;
  updatedAt: string;
  geometry: string;
  sort: number;
  timestamp: number;
  icon: string;
  color: string;
}


export class AppDatabaseItemRepo extends AppDatabaseRepo<AppDatabaseItem, SyncItem, CoreoCollectionItem> {
  tableName: string = 'items';

  columns = [
    ['id', 'INTEGER NOT NULL'],
    ['key', 'TEXT NOT NULL'],
    ['value', 'TEXT NOT NULL'],
    ['data', 'TEXT NOT NULL'],
    ['projectId', 'INTEGER NOT NULL'],
    ['collectionId', 'INTEGER NOT NULL'],
    ['createdAt', 'TEXT NOT NULL'],
    ['updatedAt', 'TEXT NOT NULL'],
    ['geometry', 'TEXT'],
    ['sort', 'INTEGER'],
    ['timestamp', 'INTEGER NOT NULL'],
    ['icon', 'TEXT'],
    ['color', 'TEXT'],
  ];
  public toInsertSet(item: SyncItem): capSQLiteSet[] {
    const columns = this.columns.map(c => c[0]).join(',');
    const values = this.columns.map(() => '?').join(',');

    const statement = `INSERT OR REPLACE INTO items (${columns}) VALUES (${values});`;

    return [{
      statement,
      values: [
        item.id,
        item.key,
        item.value,
        JSON.stringify(item.data),
        item.projectId,
        item.collectionId,
        item.createdAt,
        item.updatedAt,
        item.geometry ? JSON.stringify(item.geometry) : null,
        item.sort,
        item.timestamp,
        item.icon,
        item.color
      ]
    }];
  }

  async deserialise(entity: AppDatabaseItem): Promise<CoreoCollectionItem> {
    return {
      ...entity,
      data: entity.data && JSON.parse(entity.data),
      geometry: entity.geometry && JSON.parse(entity.geometry)
    };
  }



  indexes = [
    'CREATE INDEX IF NOT EXISTS items_projectId ON items(projectId)',
    'CREATE INDEX IF NOT EXISTS items_collectionId ON items(collectionId)'
  ];
  references = [];
  primaryKey = ["id", "projectId", "collectionId"];


  getByCollectionId(collectionId: number, projectId: number, batchSize?: number): Promise<CoreoCollectionItem[]> {
    return this.search(q => q.where('collectionId = ?', collectionId).where('projectId = ?', projectId), batchSize).toArray();
  }

  private deserializeFeature(item: AppDatabaseItem, parseItemData: boolean): Feature {
    let dataProp: unknown;
    if (parseItemData) {
      dataProp = null;
      if (item.data) {
        try {
          dataProp = JSON.parse(item.data);
        } catch {
          dataProp = null;
        }
      }
    } else {
      dataProp = item.data;
    }

    return {
      type: 'Feature',
      geometry: JSON.parse(item.geometry),
      id: item.id,
      properties: {
        id: item.id,
        collectionId: item.collectionId,
        key: item.key,
        value: item.value,
        data: dataProp,
        icon: item.icon,
        color: item.color
      }
    };
  }

  async findProjectMapFeatures(
    queryBuilder: SQLQueryBuilder,
    options?: { parseItemData?: boolean }
  ): Promise<Feature[]> {
    const parseItemData = options?.parseItemData ?? false;

    const query = queryBuilder(SQLDatabase.select().from(this.tableName)
      .field('id')
      .field('collectionId')
      .field('key')
      .field('value')
      .field('data')
      .field('icon')
      .field('color')
      .field('geometry')
      .where('geometry IS NOT NULL'));

    try {
      const features = [];
      // We query the DB directly so we don't deserialise the records first
      for await (const batch of SQLDatabase.instance.queryAll(query, 1000)) {
        const batchFeatures = batch.map(mf =>
          this.deserializeFeature(mf as any, parseItemData)
        );
        features.push(...batchFeatures);
      }

      return features;
    } catch (e) {
      console.warn(e);
      return [];
    }
  }
}
